import React from 'react';

/**
 * Props for {@link NotificationPrompt}.
 */
interface NotificationPromptProps {
  isOpen: boolean;
  /** True when the browser permission is 'denied' (cannot be re-prompted from JS). */
  blocked: boolean;
  onAccept: () => void;
  onCancel: () => void;
}

/**
 * In-app prompt shown on login when desktop-notification permission has not been
 * granted. Accept triggers the browser permission request (a real user gesture,
 * which browsers honor); Cancel dismisses it for the session. When the user has
 * previously blocked notifications, the browser will not re-prompt, so this
 * shows how to re-enable them in browser settings instead.
 */
const NotificationPrompt: React.FC<NotificationPromptProps> = ({
  isOpen,
  blocked,
  onAccept,
  onCancel,
}) => {
  if (!isOpen) return null;
  return (
    <div
      className="modal-overlay active"
      role="dialog"
      aria-modal="true"
      aria-labelledby="notification-prompt-title"
    >
      <div className="modal notification-prompt" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 id="notification-prompt-title">Enable Desktop Alerts</h2>
        </div>
        <div className="notification-prompt-body">
          {blocked ? (
            <p>
              Desktop notifications for AETHER are currently blocked in your browser. To be alerted
              when an active event reaches its Next Advise or ERTS/RTS time while the window is
              minimized or in a background tab, allow notifications for this site in your browser
              settings, then reload the page.
            </p>
          ) : (
            <p>
              Turn on desktop notifications to be alerted when an active event reaches its Next
              Advise or ERTS/RTS time while the AETHER window is minimized or in a background tab.
            </p>
          )}
        </div>
        <div className="modal-footer">
          <button className="modal-btn modal-btn-cancel" type="button" onClick={onCancel}>
            Cancel
          </button>
          {!blocked && (
            <button className="modal-btn modal-btn-save" type="button" onClick={onAccept}>
              Accept
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationPrompt;
